<script lang="ts">
	// Formatters/i18n
	import { format } from 'src/services/i18n';

	// Svelte
	import { onDestroy, tick } from 'svelte';
	import { replace } from 'svelte-spa-router';

	// Types
	import type { InputProperties } from 'src/types/InputProperties';

	// App Components — same building blocks as SubAccountSignUp
	import PasswordUtility from 'src/components/PasswordUtility.svelte';
	import SignUpConsentCheckboxes from 'src/components/Profiles/signup/SignUpConsentCheckboxes.svelte';
	import SignUpLoginPrompt from 'src/components/Profiles/signup/SignUpLoginPrompt.svelte';
	import UnitTransferCreateAccountModal from 'src/components/Settings/HomeManagement/UnitTransferCreateAccountModal.svelte';
	import TransitionWrapper from 'src/components/Utility/TransitionWrapper.svelte';

	// Design system components
	import { Button, Checkbox, Input, Text, toast } from '@Shelly/shelly-design-system/index';

	// Helpers
	import { current_time_zone } from 'src/helpers/date_helper';
	import { capFirstChar } from 'src/helpers/utilities';
	import { normalizeEmail } from 'src/helpers/email_helpers';
	import {
		DEFAULT_SIGNUP_PASSWORD_INPUT_PROPS,
		getSignUpRegisterAlert,
		showSignUpSuccessToast,
		validateSignUpConsents,
		validateSignUpPassword,
	} from 'src/helpers/signup_helpers';

	// Services
	import { completeUnitTransferRegistration } from 'src/services/iotu/house-management';
	import { handleRegister } from 'src/services/http';

	export let token = '';
	export let email = '';

	let passwordError = '';
	let isLoading = false;
	let termsCheck = false;
	let privacyPolicyCheck = false;
	let newsletterCheck = false;
	let homeConsentCheck = false;
	let showCreateAccountModal = false;

	let passwordInputProps: InputProperties = { ...DEFAULT_SIGNUP_PASSWORD_INPUT_PROPS };

	$: passwordInputProps.disabled = isLoading;
	$: hasPassword = Boolean(passwordInputProps.value?.length);
	$: canSubmit =
		!isLoading &&
		hasPassword &&
		homeConsentCheck &&
		termsCheck &&
		privacyPolicyCheck &&
		Boolean(token);

	onDestroy(() => {
		cleanUp();
	});

	async function handleSignUp(): Promise<void> {
		// Only explicit Registration click — never auto-fire on load / enter / autofill.
		if (isLoading || !canSubmit) {
			return;
		}

		email = normalizeEmail(email);

		passwordError = validateSignUpPassword(passwordInputProps.value, $format);
		if (passwordError !== '') {
			return;
		}

		if (!homeConsentCheck) {
			toast.open({
				type: 'warning',
				title: $format('error_unit_transfer_consent_required', {
					default: 'Please confirm that you consent to receive the home',
				}),
			});
			return;
		}

		if (!validateSignUpConsents(termsCheck, privacyPolicyCheck, $format)) {
			return;
		}

		isLoading = true;

		try {
			const response = await completeUnitTransferRegistration(
				token,
				email,
				passwordInputProps.value,
			);

			if (!response?.isok) {
				if (response?.errors?.transfer_expired || response?.errors?.invalid_token) {
					// Defer so the Registration click cannot fall through onto the modal CTA.
					await tick();
					showCreateAccountModal = true;
					return;
				}

				toast.open({
					type: 'danger',
					duration: 3000,
					title: $format('error_unit_transfer_registration', {
						default: 'Failed to create the account and complete the transfer',
					}),
				});
				return;
			}

			toast.open({
				type: 'success',
				title: $format('success_unit_transfer_registration', {
					default: 'Account created and home transfer completed. Please log in.',
				}),
				duration: 5000,
			});

			navigateToLogin();
		} catch {
			toast.open({
				type: 'danger',
				duration: 3000,
				title: $format('error_unit_transfer_registration', {
					default: 'Failed to create the account and complete the transfer',
				}),
			});
		} finally {
			isLoading = false;
		}
	}

	async function createAccountWithoutTransfer(): Promise<void> {
		if (isLoading || !hasPassword) {
			return;
		}

		isLoading = true;

		try {
			const res = await handleRegister(email, passwordInputProps.value, current_time_zone());

			if (!res?.isok) {
				const alert = getSignUpRegisterAlert(res, $format);
				toast.open({
					type: 'danger',
					duration: 3000,
					title: alert.message || alert.heading,
				});
				return;
			}

			showCreateAccountModal = false;
			showSignUpSuccessToast($format);
			navigateToLogin();
		} catch {
			toast.open({
				type: 'danger',
				duration: 3000,
				title: $format('error_unit_transfer_registration', {
					default: 'Failed to create the account and complete the transfer',
				}),
			});
		} finally {
			isLoading = false;
		}
	}

	function navigateToLogin(): void {
		replace('/login');
	}

	function clearErrors(): void {
		passwordError = '';
	}

	function cleanUp(): void {
		isLoading = false;
		termsCheck = false;
		privacyPolicyCheck = false;
		newsletterCheck = false;
		homeConsentCheck = false;
		showCreateAccountModal = false;
		passwordInputProps.value = '';
		passwordError = '';
	}
</script>

<TransitionWrapper>
	<Text element="h1" class="mb-l">
		{$format('unit_transfer_sign_up_header', {
			default: 'Create your account to receive the home',
		})}
	</Text>

	<Input
		id="input-unit-transfer-email"
		aria-label={capFirstChar($format('placeholder_email', { default: 'enter your e-mail' }))}
		size="l"
		class="mb-m"
		disabled={true}
		bind:value={email}
		label={capFirstChar($format('placeholder_email', { default: 'enter your e-mail' }))}
		icon="fas fa-envelope"
		type="email"
	/>

	<PasswordUtility
		bind:inputProps={passwordInputProps}
		bind:error={passwordError}
		on:focus={clearErrors}
	/>

	<Checkbox
		id="unit-transfer-home-consent"
		class="mb-s"
		size="m"
		bind:checked={homeConsentCheck}
		disabled={isLoading}
		aria-label={$format('unit_transfer_home_consent', {
			default: 'I consent to receive the home: "{name}"',
			values: { name: $format('settings_default_home_name', { default: 'My Home' }) },
		})}
	>
		{$format('unit_transfer_home_consent', {
			default: 'I consent to receive the home: "{name}"',
			values: { name: $format('settings_default_home_name', { default: 'My Home' }) },
		})}
	</Checkbox>

	<SignUpConsentCheckboxes
		idPrefix="unit-transfer"
		bind:termsCheck
		bind:privacyPolicyCheck
		bind:newsletterCheck
		disabled={isLoading}
		showDataAct={true}
		newsletterClass="mb-s"
	/>

	<Button
		class="text-capitalize"
		variant="primary"
		width="full"
		disabled={!canSubmit}
		loading={isLoading}
		aria-label={$format('button_sign_up', { default: 'Sign up' })}
		on:click={handleSignUp}
	>
		{$format('button_sign_up')}
	</Button>

	<SignUpLoginPrompt disabled={isLoading} on:loginClick={navigateToLogin} />
</TransitionWrapper>

<UnitTransferCreateAccountModal
	bind:showModal={showCreateAccountModal}
	loading={isLoading}
	on:confirm={createAccountWithoutTransfer}
/>
